import { useState } from "react";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { useLanguage, languageNames, Language } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";

export default function Header() {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();

  const navItems = [
    { 
      label: getTranslation("navServices", language), 
      href: "#", 
      hasSubmenu: true, 
      submenu: [
        { label: getTranslation("heroTitle", language), href: "/kompleksni-poslugy" },
        { label: getTranslation("navBusinessServices", language), href: "/uslugi#company" },
        { label: getTranslation("navVehicleRegistration", language), href: "/uslugi#auto" },
        { label: getTranslation("navResidencePermit", language), href: "/uslugi#support" },
      ]
    },
    { label: getTranslation("navPricing", language), href: "/cennik" },
    { label: getTranslation("navFaq", language), href: "/faq" },
    { label: getTranslation("navContact", language), href: "/kontakt" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6 md:px-8">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg sm:text-xl font-heading font-bold tracking-tight logo-gradient">1Group</span>
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        {!isMobile && (
          <nav className="mx-6 flex items-center space-x-8 lg:space-x-10">
            {navItems.map((item, i) => (
              item.hasSubmenu ? (
                <Link
                  key={i}
                  to="/uslugi"
                  className="text-sm font-medium transition-colors hover:text-primary hover-underline-dot"
                >
                  {item.label}
                </Link>
              ) : (
                <Link
                  key={i}
                  to={item.href}
                  className="text-sm font-medium transition-colors hover:text-primary hover-underline-dot"
                >
                  {item.label}
                </Link>
              )
            ))}
          </nav>
        )}
        
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language Switcher */}
          <div className="scale-90 sm:scale-100">
            <LanguageSwitcher />
          </div>
          
          {/* Quick Contact Buttons - Desktop */}
          {!isMobile && (
            <div className="flex gap-2">
              <Button 
                asChild 
                variant="outline" 
                size="sm" 
                className="button-modern text-xs sm:text-sm py-1 sm:py-1.5 px-2 sm:px-3"
              >
                <a href="mailto:kontakt1group.pl@gmail.com">
                  <Mail className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
                  {getTranslation("emailContact", language)}
                </a>
              </Button>
              <Button 
                asChild 
                size="sm" 
                className="button-modern text-xs sm:text-sm py-1 sm:py-1.5 px-2 sm:px-3"
              >
                <a href="tel:+48886695593">
                  <Phone className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
                  {getTranslation("quickContact", language)}
                </a>
              </Button>
            </div>
          )}
          
          {/* Mobile Menu Button */}
          {isMobile && (
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px] rounded-l-lg p-0">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between border-b border-border p-4">
                    <span className="text-base sm:text-lg font-heading font-semibold">{getTranslation("menu", language)}</span>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <X className="h-4 w-4 sm:h-5 sm:w-5" />
                      </Button>
                    </SheetTrigger>
                  </div>
                  <nav className="flex flex-col gap-3 sm:gap-4 p-4 overflow-y-auto flex-1">
                    {navItems.map((item, i) => (
                      item.hasSubmenu ? (
                        <Link
                          key={i}
                          to="/uslugi"
                          className="text-sm sm:text-base font-medium transition-colors hover:text-primary"
                          onClick={() => setIsOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <Link
                          key={i}
                          to={item.href}
                          className="text-sm sm:text-base font-medium transition-colors hover:text-primary"
                          onClick={() => setIsOpen(false)}
                        >
                          {item.label}
                        </Link>
                      )
                    ))}
                  </nav>
                  <div className="p-4 space-y-4 border-t border-border">
                    {/* Mobile Language Switcher */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">{getTranslation("languageSwitchLabel", language)}</span>
                      <div className="flex gap-1 sm:gap-2">
                        {(Object.keys(languageNames) as Array<keyof typeof languageNames>).map((lang) => (
                          <Button
                            key={lang}
                            variant={useLanguage.getState().language === lang ? "default" : "outline"}
                            size="sm"
                            onClick={() => useLanguage.getState().setLanguage(lang)}
                            className="h-7 sm:h-8 px-1.5 sm:px-2 text-xs"
                          >
                            {lang.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Quick Contact Buttons - Mobile Menu */}
                    <div className="flex flex-col gap-2">
                      <Button 
                        asChild 
                        className="w-full button-modern text-xs sm:text-sm py-1.5 sm:py-2"
                      >
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button 
                        asChild 
                        variant="outline"
                        className="w-full button-modern text-xs sm:text-sm py-1.5 sm:py-2"
                      >
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
}